import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown2, DocumentText } from 'iconsax-react';
import dayjs from 'dayjs';

import Button from '../../../components/atoms/Button';
import Chip from '../../../components/atoms/Chip';
import TitleText from '../../../components/atoms/TitleText';
import InfoText from '../../../components/atoms/InfoText';
import { Modal } from 'antd';
import { SupportTicket } from '../../../types/support';
import { formatActivityDate, formatDocumentUrl, getStatusType } from './utils';
import { TicketUserCards } from './TicketUserCards';

const isImageFile = (name: string) =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(name ?? '');

const isPdfFile = (name: string) => /\.pdf(\?|$)/i.test(name ?? '');

interface TicketHistoryProps {
  historyTickets: SupportTicket[];
  isLoading: boolean;
  onOpenChatHistory?: (roomId: string) => void;
}

export const TicketHistory: React.FC<TicketHistoryProps> = ({
  historyTickets,
  isLoading,
  onOpenChatHistory,
}) => {
  const { t } = useTranslation();
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(
    null
  );

  const isImage = preview ? isImageFile(preview.name) : false;

  const handlePreview = (url: string, name: string) => {
    const formattedUrl = formatDocumentUrl(url);
    if (isPdfFile(name)) {
      const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        formattedUrl
      )}&embedded=true`;
      window.open(previewUrl, '_blank');
    } else {
      setPreview({ url: formattedUrl, name });
    }
  };

  const toggleHistoryItem = (id: string) => {
    setExpandedHistoryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <div data-testid="ticket-history-container">
      <TitleText
        text={t('support.ticket_history')}
        size="xl"
        className="!font-bold !text-light-text-primary dark:!text-dark-text-primary mb-4"
        data-testid="ticket-history-title"
      />

      {isLoading ? (
        <div className="flex flex-col gap-3" data-testid="ticket-history-loading-list">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 bg-white dark:bg-dark-surface-primary animate-pulse flex justify-between items-center"
              data-testid={`ticket-history-skeleton-${i}`}
            >
              <div className="flex flex-col gap-2">
                <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded" />
              </div>
              <div className="h-6 w-20 bg-gray-100 dark:bg-gray-800 rounded-full" />
            </div>
          ))}
        </div>
      ) : historyTickets.length > 0 ? (
        <div className="flex flex-col gap-3" data-testid="ticket-history-list">
          {historyTickets.map((historyItem) => {
            const isExpanded = expandedHistoryIds.includes(historyItem.id);
            const historyItemDate = historyItem.createdDate
              ? dayjs(historyItem.createdDate).isValid()
                ? dayjs(historyItem.createdDate).format('DD MMM YYYY')
                : historyItem.createdDate
              : '---';

            return (
              <div
                key={historyItem.id}
                className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden bg-white dark:bg-dark-surface-primary hover:border-gray-200 dark:hover:border-gray-700 transition-colors shadow-sm"
                data-testid={`ticket-history-card-${historyItem.id}`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleHistoryItem(historyItem.id)}
                  className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                  data-testid={`ticket-history-header-${historyItem.id}`}
                >
                  <div data-testid={`ticket-history-header-info-${historyItem.id}`}>
                    <TitleText
                      text={historyItem.reason?.name || historyItem.serial || '---'}
                      size="base"
                      className="!font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-1"
                      data-testid={`ticket-history-item-title-${historyItem.id}`}
                    />
                    <InfoText
                      text={t('support.ticket_meta_info', {
                        serial:
                          historyItem.serial?.replace(/^#/, '') ||
                          historyItem.id,
                        date: historyItemDate,
                      })}
                      size="sm"
                      className="!text-light-text-secondary dark:!text-dark-text-secondary"
                      maxLength="FULL"
                      data-testid={`ticket-history-item-meta-${historyItem.id}`}
                    />
                  </div>

                  <div
                    className="flex items-center gap-3"
                    data-testid={`ticket-history-status-container-${historyItem.id}`}
                  >
                    <Chip
                      type={getStatusType(historyItem.status?.code)}
                      className="!rounded-full px-3 py-0.5 text-xs font-medium"
                      data-testid={`ticket-history-status-chip-${historyItem.id}`}
                    >
                      {historyItem.status?.code
                        ? t(`support.${historyItem.status.code}`)
                        : historyItem.status?.name || '---'}
                    </Chip>
                    <ArrowDown2
                      size={18}
                      className={`text-gray-500 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : 'rotate-0'
                      }`}
                      data-testid={`ticket-history-toggle-icon-${historyItem.id}`}
                    />
                  </div>
                </div>

                {isExpanded && (
                  <div
                    className="p-4 pt-2 border-t border-gray-100 dark:border-gray-800 space-y-4 min-w-0"
                    data-testid={`ticket-history-body-${historyItem.id}`}
                  >
                    {historyItem.description && (
                      <InfoText
                        text={historyItem.description}
                        size="sm"
                        className="!text-light-text-primary dark:!text-dark-text-primary leading-relaxed whitespace-pre-line break-words"
                        maxLength="FULL"
                        data-testid={`ticket-history-description-${historyItem.id}`}
                      />
                    )}

                    {/* Requester & Against User (Seller) Details */}
                    <TicketUserCards
                      ticket={historyItem}
                      testIdPrefix={`ticket-history-users-${historyItem.id}`}
                    />

                    {historyItem.documentInfoDtos &&
                      historyItem.documentInfoDtos.length > 0 && (
                        <div data-testid={`ticket-history-attachments-${historyItem.id}`}>
                          <TitleText
                            text={t('support.attachment_single')}
                            size="xs"
                            className="!font-bold !text-light-text-secondary dark:!text-dark-text-secondary uppercase tracking-wider mb-2"
                            data-testid={`ticket-history-attachments-title-${historyItem.id}`}
                          />
                          <div className="flex flex-col gap-2">
                            {historyItem.documentInfoDtos.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between border border-gray-100 dark:border-gray-800 rounded-xl p-2.5 bg-gray-50/50 dark:bg-gray-800/40"
                                data-testid={`ticket-history-attachment-item-${doc.id}`}
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <DocumentText
                                    size={18}
                                    className="text-gray-500 shrink-0"
                                  />
                                  <InfoText
                                    text={doc.name || doc.blobName || '---'}
                                    size="xs"
                                    className="!font-medium !text-light-text-primary dark:!text-dark-text-primary truncate"
                                    maxLength="FULL"
                                    data-testid={`ticket-history-attachment-name-${doc.id}`}
                                  />
                                </div>
                                {doc.blobFileUrl && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handlePreview(
                                        doc.blobFileUrl!,
                                        doc.name || doc.blobName || 'Document'
                                      )
                                    }
                                    className="text-light-information dark:text-dark-information text-xs hover:underline shrink-0"
                                    data-testid={`ticket-history-attachment-view-btn-${doc.id}`}
                                  >
                                    {t('support.view')}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {historyItem.activityDtos &&
                      historyItem.activityDtos.length > 0 && (
                        <div data-testid={`ticket-history-activities-${historyItem.id}`}>
                          <TitleText
                            text={t('support.ticket_activity_label')}
                            size="xs"
                            className="!font-bold !text-light-text-secondary dark:!text-dark-text-secondary uppercase tracking-wider mb-2"
                            data-testid={`ticket-history-activities-title-${historyItem.id}`}
                          />
                          <div className="flex flex-col gap-3">
                            {historyItem.activityDtos.map((act) => (
                              <div
                                key={act.id}
                                className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 text-xs min-w-0"
                                data-testid={`ticket-history-activity-item-${act.id}`}
                              >
                                <div
                                  className="flex justify-between items-start gap-2 min-w-0"
                                  data-testid={`ticket-history-activity-header-${act.id}`}
                                >
                                  <div
                                    className="flex-1 min-w-0 break-words"
                                    data-testid={`ticket-history-activity-content-${act.id}`}
                                  >
                                    <span
                                      className="font-semibold text-light-text-primary dark:text-dark-text-primary"
                                      data-testid={`ticket-history-activity-action-${act.id}`}
                                    >
                                      {act.action?.name || '---'}:
                                    </span>{' '}
                                    <span
                                      className="text-light-text-secondary dark:text-dark-text-secondary break-words whitespace-pre-line"
                                      data-testid={`ticket-history-activity-message-${act.id}`}
                                    >
                                      {act.msg || act.message || '---'}
                                    </span>
                                  </div>
                                  {(act.chatRoomId || act.roomId) && (
                                    <Button
                                      variant="outline"
                                      className="!py-1 !px-2 text-xs rounded bg-white dark:bg-dark-surface-primary shrink-0"
                                      data-testid={`ticket-history-activity-chat-btn-${act.id}`}
                                      onClick={() => {
                                        onOpenChatHistory?.(
                                          (act.chatRoomId || act.roomId)!
                                        );
                                      }}
                                    >
                                      {t('support.seeChatHistory')}
                                    </Button>
                                  )}
                                </div>
                                <span
                                  className="text-gray-400 mt-1 block"
                                  data-testid={`ticket-history-activity-date-${act.id}`}
                                >
                                  {formatActivityDate(act.createdDate, t)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <InfoText
          text={t('support.no_history_found')}
          size="sm"
          className="text-gray-400 py-3 italic"
          data-testid="ticket-history-empty"
        />
      )}

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 900 }}
        title={preview?.name}
        destroyOnHidden
        centered
        data-testid="ticket-history-preview-modal"
      >
        {preview && (
          <div
            className="flex justify-center bg-gray-50 dark:bg-gray-900 rounded-lg min-h-[60vh] overflow-auto"
            data-testid="ticket-history-preview-content"
          >
            {isImage ? (
              <img
                src={preview.url}
                alt={preview.name}
                className="max-w-full max-h-[70vh] object-contain"
                data-testid="ticket-history-preview-image"
              />
            ) : (
              <iframe
                title={preview.name}
                src={preview.url}
                className="w-full min-h-[60vh] border-0 rounded-lg"
                data-testid="ticket-history-preview-iframe"
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TicketHistory;
